/**
 * Add liquidity to a market (operator only)
 */
import type { Address, Hash, WalletClient } from "viem"
import { breakoutMarketAbi } from "../../abi/generated/breakoutMarket"
import { getAccount } from "../../internal/contracts"
import { toUsdcScale } from "../../internal/convert"

export interface AddLiquidityParams {
	market: Address
	/** Amount of USDC to add (human-readable, e.g., 1000 for $1000) */
	amount: number
}

/**
 * Add liquidity to a market
 *
 * Can only be called by the operator. Requires USDC approval.
 * Increases the market's liquidity parameter (b), reducing price volatility.
 *
 * @example
 * ```ts
 * import { addLiquidity } from "@breakout/sdk/actions/write"
 *
 * // First approve USDC for the market contract
 * const txHash = await addLiquidity(walletClient, {
 *   market: "0x...",
 *   amount: 1000, // Add $1000 USDC
 * })
 * ```
 */
export async function addLiquidity(
	client: WalletClient,
	params: AddLiquidityParams,
): Promise<Hash> {
	const account = await getAccount(client)

	return client.writeContract({
		address: params.market,
		abi: breakoutMarketAbi,
		functionName: "addLiquidity",
		args: [toUsdcScale(params.amount)],
		account,
		chain: client.chain,
	})
}
